import { useQuery, queryOptions } from "@tanstack/react-query";
import { logisticsApi } from "./ApiClient";
import { generateQueryString } from "./Helpers";
import {
  scheduledShiftSchema,
  medicalCompetenceSchema,
  employee,
} from "@medoma/models/shifts";
import { machineFormat } from "@medoma/models/date-and-time";
import type { SiteId } from "@medoma/models/sites";
import { z } from "zod";
import { deduplicatePrimitiveArray } from "@/Utils/arrayUtils";
import { fetchPatients, resolvePatient } from "./Patients";
import { categorySchema } from "@medoma/models/activities";
import { useUserProfile } from "./Users";
import {
  classifyShiftItem,
  shiftItemKindSchema,
  type IShiftItem,
} from "@medoma/models/shiftWithContents";
import {
  unenrichedShiftWithContentsSchema,
  type IUnenrichedShiftItem,
} from "@medoma/models/shiftWithContents.unenriched";

const shiftKeys = {
  all: ["shifts"] as const,
  lists: () => [...shiftKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...shiftKeys.lists(), filters] as const,
  listWithContents: (filters: Record<string, unknown>) =>
    [...shiftKeys.lists(), "contents", filters] as const,
  listForSite: (filters: Record<string, unknown>) =>
    [...shiftKeys.lists(), "for-site", filters] as const,
};

async function fetchEnabledCompetences() {
  const parsedEnabledCompetences = await logisticsApi
    .get("/competences")
    .json(z.array(medicalCompetenceSchema));

  return parsedEnabledCompetences;
}

const competenceKeys = {
  all: ["enabledCompetences"] as const,
};

export const useEnabledCompetences = () => {
  return useQuery({
    queryKey: competenceKeys.all,
    queryFn: fetchEnabledCompetences,
  });
};

async function fetchShiftsWithContents(date: string) {
  const parsedUnenrichedShiftsWithContents = await logisticsApi
    .get(`/ambulating/schedule/planned${generateQueryString({ date })}`)
    .json(z.array(unenrichedShiftWithContentsSchema));

  // AdminTask activities sometimes miss patientId
  const patientIds = deduplicatePrimitiveArray(
    parsedUnenrichedShiftsWithContents.flatMap(({ items }) =>
      items.flatMap((item) => {
        const shiftItem = classifyShiftItem(item);
        switch (shiftItem.kind) {
          case shiftItemKindSchema.enum.route:
            return shiftItem.item.visits.map((visit) => visit.patientId);
          case shiftItemKindSchema.enum.group:
            return [shiftItem.item.patientId];
          case shiftItemKindSchema.enum.occurrence:
            return shiftItem.item.patientId ? [shiftItem.item.patientId] : [];
          default:
            throw shiftItem satisfies never;
        }
      }),
    ),
  );

  const patients = await fetchPatients({ patientIds });

  const enrichShiftItem = (item: IUnenrichedShiftItem): IShiftItem => {
    const shiftItem = classifyShiftItem(item);
    switch (shiftItem.kind) {
      case shiftItemKindSchema.enum.route:
        return {
          ...shiftItem.item,
          visits: shiftItem.item.visits.map(({ patientId, ...visit }) => ({
            ...visit,
            patient: resolvePatient({ patientId, patients }),
          })),
        };
      case shiftItemKindSchema.enum.group: {
        const { patientId, ...group } = shiftItem.item;
        const patient = resolvePatient({ patientId, patients });
        return {
          ...group,
          patient,
          occurrences: group.occurrences.map(
            ({ patientId: _droppedPatientId, ...occurrence }) => ({
              ...occurrence,
              patient,
            }),
          ),
        };
      }
      case shiftItemKindSchema.enum.occurrence: {
        const occurrence = shiftItem.item;
        // An admin task is the only category that can come without a patient.
        if (occurrence.category === categorySchema.enum.AdminTask) {
          const { patientId, ...adminTask } = occurrence;
          return {
            ...adminTask,
            patient: patientId ? resolvePatient({ patientId, patients }) : null,
          };
        }
        const { patientId, ...rest } = occurrence;
        return {
          ...rest,
          patient: resolvePatient({ patientId, patients }),
        };
      }
      default:
        throw shiftItem satisfies never;
    }
  };

  const enrichedShifts = parsedUnenrichedShiftsWithContents.map(
    (unenrichedShift) => ({
      ...unenrichedShift,
      items: unenrichedShift.items.map(enrichShiftItem),
    }),
  );

  return enrichedShifts;
}

const shiftsWithContentsQueryOptions = (selectedDate: Date) => {
  const formattedDate = machineFormat(selectedDate, "yyyy-MM-dd");

  return queryOptions({
    queryKey: shiftKeys.listWithContents({
      date: formattedDate,
    }),
    queryFn: () => fetchShiftsWithContents(formattedDate),
  });
};

export const useShiftsWithContents = (selectedDate: Date) => {
  // This fetches data for all shifts. The goal is to only fetch data for "my" shift
  // Refactor when BE in place for this (new endpoint)
  return useQuery(shiftsWithContentsQueryOptions(selectedDate));
};

export const useMyRoutesAndActivities = (selectedDate: Date) => {
  const { employeeNumber } = useUserProfile();
  const myEmployeeId = employee.shape.id.parse(Number(employeeNumber));
  return useQuery({
    ...shiftsWithContentsQueryOptions(selectedDate),
    select: (shifts) =>
      shifts
        .filter(
          (shiftWithContents) =>
            shiftWithContents.shift.employee?.id === myEmployeeId,
        )
        .sort(
          (a, b) =>
            a.shift.startDateTime.getTime() - b.shift.startDateTime.getTime(),
        )
        .flatMap((shiftWithContents) => shiftWithContents.items),
  });
};

async function fetchShiftsForSite(date: string, siteId: SiteId) {
  return logisticsApi
    .get(`/ambulating/schedule${generateQueryString({ date, siteId })}`)
    .json(z.array(scheduledShiftSchema));
}

const shiftsForSiteQueryOptions = (selectedDate: Date, siteId: SiteId) => {
  const formattedDate = machineFormat(selectedDate, "yyyy-MM-dd");

  return queryOptions({
    queryKey: shiftKeys.listForSite({ date: formattedDate, siteId }),
    queryFn: () => fetchShiftsForSite(formattedDate, siteId),
  });
};

export const useShiftsForSite = (selectedDate: Date, siteId: SiteId) => {
  return useQuery(shiftsForSiteQueryOptions(selectedDate, siteId));
};

export async function assignGroupToShift(groupId: string, shiftId: number) {
  await logisticsApi.post(`/ambulating/shifts/${shiftId}/assign`, {
    json: { occurrenceGroupId: groupId },
  });
}

export async function unassignGroupFromShift(groupId: string, shiftId: number) {
  await logisticsApi.post(`/ambulating/shifts/${shiftId}/unassign`, {
    json: { occurrenceGroupId: groupId },
  });
}
